import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

export interface Shop {
    id: string;
    name: string;
    domain: string;
    createdAt: Date;
}

interface ShopContextType {
    shops: Shop[];
    selectedShop: Shop | null;
    isLoading: boolean;
    selectShop: (shop: Shop) => void;
    refreshShops: () => Promise<void>;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

export function ShopProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const [shops, setShops] = useState<Shop[]>([]);
    const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchShops = async () => {
        if (!user) {
            setShops([]);
            setSelectedShop(null);
            return;
        }

        setIsLoading(true);

        try {
            // Simple query without orderBy to avoid index requirements
            const snapshot = await getDocs(collection(db, 'shops'));

            const shopsList: Shop[] = snapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name || 'Unnamed Shop',
                domain: doc.data().domain || '',
                createdAt: doc.data().createdAt?.toDate() || new Date(),
            }));

            // Sort client-side
            shopsList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

            setShops(shopsList);

            // Auto-select first shop if none selected
            if (shopsList.length > 0 && !selectedShop) {
                setSelectedShop(shopsList[0]);
            }
        } catch (error) {
            console.error('Error fetching shops:', error);
            // Don't break the app, just set empty shops
            setShops([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        // Only fetch when user is available
        if (user) {
            fetchShops();
        } else {
            setShops([]);
            setSelectedShop(null);
        }
    }, [user]);

    const selectShop = (shop: Shop) => {
        setSelectedShop(shop);
    };

    const refreshShops = async () => {
        await fetchShops();
    };

    return (
        <ShopContext.Provider
            value={{
                shops,
                selectedShop,
                isLoading,
                selectShop,
                refreshShops,
            }}
        >
            {children}
        </ShopContext.Provider>
    );
}

export function useShops() {
    const context = useContext(ShopContext);
    if (context === undefined) {
        throw new Error('useShops must be used within a ShopProvider');
    }
    return context;
}
